import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { isAuthorizationError, isAuthenticationError, clearAuthData } from '../utils/authUtils';

// Error types
const ERROR_TYPES = {
    API: 'api',
    SOCKET: 'socket',
    AUTH: 'auth',
    VALIDATION: 'validation',
    NETWORK: 'network',
    UNKNOWN: 'unknown'
};

// Error severity levels
const ERROR_SEVERITY = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
};

// Initial state
const initialState = {
    errors: [],
    isErrorModalOpen: false,
    currentError: null,
    errorCount: 0
};

// Action types
const ERROR_ACTIONS = {
    ADD_ERROR: 'ADD_ERROR',
    REMOVE_ERROR: 'REMOVE_ERROR',
    CLEAR_ERRORS: 'CLEAR_ERRORS',
    SET_ERROR_MODAL: 'SET_ERROR_MODAL',
    SET_CURRENT_ERROR: 'SET_CURRENT_ERROR',
    INCREMENT_ERROR_COUNT: 'INCREMENT_ERROR_COUNT'
};

// Error reducer
const errorReducer = (state, action) => {
    switch (action.type) {
        case ERROR_ACTIONS.ADD_ERROR:
            return {
                ...state,
                errors: [...state.errors, action.payload],
                errorCount: state.errorCount + 1
            };

        case ERROR_ACTIONS.REMOVE_ERROR:
            return {
                ...state,
                errors: state.errors.filter(error => error.id !== action.payload),
                errorCount: Math.max(0, state.errorCount - 1)
            };

        case ERROR_ACTIONS.CLEAR_ERRORS:
            return {
                ...state,
                errors: [],
                errorCount: 0,
                isErrorModalOpen: false,
                currentError: null
            };

        case ERROR_ACTIONS.SET_ERROR_MODAL:
            return {
                ...state,
                isErrorModalOpen: action.payload
            };

        case ERROR_ACTIONS.SET_CURRENT_ERROR:
            return {
                ...state,
                currentError: action.payload
            };

        case ERROR_ACTIONS.INCREMENT_ERROR_COUNT:
            return {
                ...state,
                errorCount: state.errorCount + 1
            };

        default:
            return state;
    }
};

// Create context
const ErrorContext = createContext();

// Error provider component
export const ErrorProvider = ({ children }) => {
    const [state, dispatch] = useReducer(errorReducer, initialState);

    // Generate unique error ID
    const generateErrorId = useCallback(() => {
        return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    // Determine error type based on error object
    const determineErrorType = useCallback((error) => {
        if (isAuthenticationError(error)) return ERROR_TYPES.AUTH;
        if (isAuthorizationError(error)) return ERROR_TYPES.AUTH;
        if (error.code === 'ERR_NETWORK' || error.message?.includes('network')) return ERROR_TYPES.NETWORK;
        if (error.response?.status >= 400 && error.response?.status < 500) return ERROR_TYPES.VALIDATION;
        if (error.response?.status >= 500) return ERROR_TYPES.API;
        if (error.socket || error.message?.includes('socket')) return ERROR_TYPES.SOCKET;
        return ERROR_TYPES.UNKNOWN;
    }, []);

    // Determine error severity
    const determineErrorSeverity = useCallback((error, type) => {
        if (type === ERROR_TYPES.AUTH) return ERROR_SEVERITY.HIGH;
        if (type === ERROR_TYPES.NETWORK) return ERROR_SEVERITY.MEDIUM;
        if (error.response?.status >= 500) return ERROR_SEVERITY.HIGH;
        if (error.response?.status >= 400) return ERROR_SEVERITY.MEDIUM;
        return ERROR_SEVERITY.LOW;
    }, []);

    // Create user-friendly error message
    const createUserFriendlyMessage = useCallback((error, type) => {
        if (isAuthenticationError(error)) {
            return 'Your session has expired. Please log in again.';
        }
        if (isAuthorizationError(error)) {
            return 'You do not have permission to perform this action.';
        }
        if (type === ERROR_TYPES.NETWORK) {
            return 'Connection issue. Please check your internet and try again.';
        }
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        if (error.message) {
            // FIXED: Provide user-friendly messages instead of technical ones
            if (error.message.includes('timeout')) {
                return 'The request took too long. Please try again.';
            }
            if (error.message.includes('network')) {
                return 'Connection issue. Please check your internet and try again.';
            }
            if (error.message.includes('server')) {
                return 'Service temporarily unavailable. Please try again later.';
            }
            return error.message;
        }
        return 'Something went wrong. Please try again.';
    }, []);

    // Add error to global error state
    const addError = useCallback((error, options = {}) => {
        const errorId = generateErrorId();
        const type = determineErrorType(error);
        const severity = determineErrorSeverity(error, type);
        const userMessage = createUserFriendlyMessage(error, type);

        // FIXED: Filter out technical errors that shouldn't be shown to users
        const shouldShowToUser = (error, type, severity) => {
            // Always show authentication and authorization errors
            if (type === ERROR_TYPES.AUTH) {
                console.log('Showing auth error to user:', error.message);
                return true;
            }

            // Always show critical errors
            if (severity === ERROR_SEVERITY.CRITICAL) {
                console.log('Showing critical error to user:', error.message);
                return true;
            }

            // Show validation errors (user input issues)
            if (type === ERROR_TYPES.VALIDATION) {
                console.log('Showing validation error to user:', error.message);
                return true;
            }

            // Don't show technical errors to users unless they're critical
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                console.warn('Suppressing timeout error from user display:', error.message);
                return false;
            }

            if (error.code === 'ERR_NETWORK' || error.message?.includes('network')) {
                console.warn('Suppressing network error from user display:', error.message);
                return false;
            }

            if (error.response?.status >= 500) {
                console.warn('Suppressing server error from user display:', error.message);
                return false;
            }

            // Don't show generic fetch errors
            if (error.message?.includes('Failed to fetch')) {
                console.warn('Suppressing fetch error from user display:', error.message);
                return false;
            }

            // Don't show generic error messages
            if (error.message?.includes('An unexpected error occurred') ||
                error.message?.includes('Something went wrong')) {
                console.warn('Suppressing generic error from user display:', error.message);
                return false;
            }

            // Don't show connection refused errors
            if (error.message?.includes('connection refused') ||
                error.message?.includes('ECONNREFUSED')) {
                console.warn('Suppressing connection refused error from user display:', error.message);
                return false;
            }

            // Don't show DNS resolution errors
            if (error.message?.includes('ENOTFOUND') ||
                error.message?.includes('getaddrinfo')) {
                console.warn('Suppressing DNS error from user display:', error.message);
                return false;
            }

            // Don't show certificate errors
            if (error.message?.includes('certificate') ||
                error.message?.includes('SSL') ||
                error.message?.includes('TLS')) {
                console.warn('Suppressing certificate error from user display:', error.message);
                return false;
            }

            // Don't show "User not found" errors when user is not authenticated
            if (error.message?.includes('User not found') ||
                error.message?.includes('User not found or resource does not exist') ||
                error.message?.includes('user not found') ||
                error.message?.includes('resource does not exist')) {
                console.warn('Suppressing user not found error from user display:', error.message);
                return false;
            }

            // Don't show 404 errors for user-related operations when not authenticated
            if (error.response?.status === 404 &&
                (error.message?.includes('User') ||
                    error.message?.includes('user') ||
                    error.config?.url?.includes('/auth/user/') ||
                    error.config?.url?.includes('/api/auth/user/'))) {
                console.warn('Suppressing 404 user error from user display:', error.message);
                return false;
            }

            // Don't show "User not found" errors when user is authenticated but not in database
            // This happens during signup or when backend sync fails
            if (error.message?.includes('User not found') &&
                error.response?.status === 404 &&
                (error.config?.url?.includes('/auth/user/') ||
                    error.config?.url?.includes('/api/auth/user/'))) {
                console.warn('Suppressing authenticated user not found in database error:', error.message);
                return false;
            }

            // Show other errors by default
            console.log('Showing error to user:', error.message);
            return true;
        };

        const errorObject = {
            id: errorId,
            type,
            severity,
            message: userMessage,
            originalError: error,
            timestamp: new Date().toISOString(),
            source: options.source || 'unknown',
            retryable: options.retryable !== false,
            autoDismiss: options.autoDismiss !== false,
            ...options
        };

        // Log all errors for debugging
        console.error('Global error caught:', {
            id: errorId,
            type,
            severity,
            message: userMessage,
            originalError: error,
            willShowToUser: shouldShowToUser(error, type, severity)
        });

        // Only add to global state if it should be shown to user
        if (shouldShowToUser(error, type, severity)) {
            dispatch({ type: ERROR_ACTIONS.ADD_ERROR, payload: errorObject });

            // Auto-dismiss low severity errors after 5 seconds
            if (errorObject.autoDismiss && severity === ERROR_SEVERITY.LOW) {
                setTimeout(() => {
                    removeError(errorId);
                }, 5000);
            }

            // Handle critical errors (show modal)
            if (severity === ERROR_SEVERITY.CRITICAL) {
                dispatch({ type: ERROR_ACTIONS.SET_CURRENT_ERROR, payload: errorObject });
                dispatch({ type: ERROR_ACTIONS.SET_ERROR_MODAL, payload: true });
            }
        }

        // Handle auth errors (clear auth data) regardless of display
        if (type === ERROR_TYPES.AUTH) {
            clearAuthData();
        }

        return errorId;
    }, [generateErrorId, determineErrorType, determineErrorSeverity, createUserFriendlyMessage]);

    // Remove error by ID
    const removeError = useCallback((errorId) => {
        dispatch({ type: ERROR_ACTIONS.REMOVE_ERROR, payload: errorId });
    }, []);

    // Clear all errors
    const clearErrors = useCallback(() => {
        dispatch({ type: ERROR_ACTIONS.CLEAR_ERRORS });
    }, []);

    // Close error modal
    const closeErrorModal = useCallback(() => {
        dispatch({ type: ERROR_ACTIONS.SET_ERROR_MODAL, payload: false });
        dispatch({ type: ERROR_ACTIONS.SET_CURRENT_ERROR, payload: null });
    }, []);

    // Handle async operations with error catching
    const handleAsync = useCallback(async (asyncOperation, options = {}) => {
        try {
            return await asyncOperation();
        } catch (error) {
            addError(error, options);
            throw error; // Re-throw so components can still handle if needed
        }
    }, [addError]);

    // Handle API calls with error catching
    const handleApiCall = useCallback(async (apiCall, options = {}) => {
        try {
            return await apiCall();
        } catch (error) {
            // FIXED: Use the same filtering logic as addError to avoid showing technical errors
            const type = determineErrorType(error);
            const severity = determineErrorSeverity(error, type);

            // Only add to global state if it should be shown to user
            const shouldShowToUser = (error, type, severity) => {
                // Always show authentication and authorization errors
                if (type === ERROR_TYPES.AUTH) {
                    console.log('Showing auth error to user:', error.message);
                    return true;
                }

                // Always show critical errors
                if (severity === ERROR_SEVERITY.CRITICAL) {
                    console.log('Showing critical error to user:', error.message);
                    return true;
                }

                // Show validation errors (user input issues)
                if (type === ERROR_TYPES.VALIDATION) {
                    console.log('Showing validation error to user:', error.message);
                    return true;
                }

                // Don't show technical errors to users unless they're critical
                if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                    console.warn('Suppressing timeout error from user display:', error.message);
                    return false;
                }

                if (error.code === 'ERR_NETWORK' || error.message?.includes('network')) {
                    console.warn('Suppressing network error from user display:', error.message);
                    return false;
                }

                if (error.response?.status >= 500) {
                    console.warn('Suppressing server error from user display:', error.message);
                    return false;
                }

                // Don't show generic fetch errors
                if (error.message?.includes('Failed to fetch')) {
                    console.warn('Suppressing fetch error from user display:', error.message);
                    return false;
                }

                // Don't show generic error messages
                if (error.message?.includes('An unexpected error occurred') ||
                    error.message?.includes('Something went wrong')) {
                    console.warn('Suppressing generic error from user display:', error.message);
                    return false;
                }

                // Don't show connection refused errors
                if (error.message?.includes('connection refused') ||
                    error.message?.includes('ECONNREFUSED')) {
                    console.warn('Suppressing connection refused error from user display:', error.message);
                    return false;
                }

                // Don't show DNS resolution errors
                if (error.message?.includes('ENOTFOUND') ||
                    error.message?.includes('getaddrinfo')) {
                    console.warn('Suppressing DNS error from user display:', error.message);
                    return false;
                }

                // Don't show certificate errors
                if (error.message?.includes('certificate') ||
                    error.message?.includes('SSL') ||
                    error.message?.includes('TLS')) {
                    console.warn('Suppressing certificate error from user display:', error.message);
                    return false;
                }

                // Don't show "User not found" errors when user is not authenticated
                if (error.message?.includes('User not found') ||
                    error.message?.includes('User not found or resource does not exist') ||
                    error.message?.includes('user not found') ||
                    error.message?.includes('resource does not exist')) {
                    console.warn('Suppressing user not found error from user display:', error.message);
                    return false;
                }

                // Don't show 404 errors for user-related operations when not authenticated
                if (error.response?.status === 404 &&
                    (error.message?.includes('User') ||
                        error.message?.includes('user') ||
                        error.config?.url?.includes('/auth/user/') ||
                        error.config?.url?.includes('/api/auth/user/'))) {
                    console.warn('Suppressing 404 user error from user display:', error.message);
                    return false;
                }

                // Don't show "User not found" errors when user is authenticated but not in database
                // This happens during signup or when backend sync fails
                if (error.message?.includes('User not found') &&
                    error.response?.status === 404 &&
                    (error.config?.url?.includes('/auth/user/') ||
                        error.config?.url?.includes('/api/auth/user/'))) {
                    console.warn('Suppressing authenticated user not found in database error:', error.message);
                    return false;
                }

                // Show other errors by default
                console.log('Showing error to user:', error.message);
                return true;
            };

            // Log all errors for debugging
            console.error('API call error caught:', {
                type,
                severity,
                message: error.message,
                originalError: error,
                willShowToUser: shouldShowToUser(error, type, severity)
            });

            // Only add to global state if it should be shown to user
            if (shouldShowToUser(error, type, severity)) {
                addError(error, {
                    type: ERROR_TYPES.API,
                    source: 'api',
                    ...options
                });
            }

            throw error;
        }
    }, [addError, determineErrorType, determineErrorSeverity]);

    // Handle socket operations with error catching
    const handleSocketCall = useCallback(async (socketCall, options = {}) => {
        try {
            return await socketCall();
        } catch (error) {
            // FIXED: Use the same filtering logic as addError to avoid showing technical errors
            const type = determineErrorType(error);
            const severity = determineErrorSeverity(error, type);

            // Only add to global state if it should be shown to user
            const shouldShowToUser = (error, type, severity) => {
                // Always show authentication and authorization errors
                if (type === ERROR_TYPES.AUTH) {
                    console.log('Showing auth error to user:', error.message);
                    return true;
                }

                // Always show critical errors
                if (severity === ERROR_SEVERITY.CRITICAL) {
                    console.log('Showing critical error to user:', error.message);
                    return true;
                }

                // Show validation errors (user input issues)
                if (type === ERROR_TYPES.VALIDATION) {
                    console.log('Showing validation error to user:', error.message);
                    return true;
                }

                // Don't show technical errors to users unless they're critical
                if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                    console.warn('Suppressing timeout error from user display:', error.message);
                    return false;
                }

                if (error.code === 'ERR_NETWORK' || error.message?.includes('network')) {
                    console.warn('Suppressing network error from user display:', error.message);
                    return false;
                }

                if (error.response?.status >= 500) {
                    console.warn('Suppressing server error from user display:', error.message);
                    return false;
                }

                // Don't show generic fetch errors
                if (error.message?.includes('Failed to fetch')) {
                    console.warn('Suppressing fetch error from user display:', error.message);
                    return false;
                }

                // Don't show generic error messages
                if (error.message?.includes('An unexpected error occurred') ||
                    error.message?.includes('Something went wrong')) {
                    console.warn('Suppressing generic error from user display:', error.message);
                    return false;
                }

                // Don't show connection refused errors
                if (error.message?.includes('connection refused') ||
                    error.message?.includes('ECONNREFUSED')) {
                    console.warn('Suppressing connection refused error from user display:', error.message);
                    return false;
                }

                // Don't show DNS resolution errors
                if (error.message?.includes('ENOTFOUND') ||
                    error.message?.includes('getaddrinfo')) {
                    console.warn('Suppressing DNS error from user display:', error.message);
                    return false;
                }

                // Don't show certificate errors
                if (error.message?.includes('certificate') ||
                    error.message?.includes('SSL') ||
                    error.message?.includes('TLS')) {
                    console.warn('Suppressing certificate error from user display:', error.message);
                    return false;
                }

                // Don't show "User not found" errors when user is not authenticated
                if (error.message?.includes('User not found') ||
                    error.message?.includes('User not found or resource does not exist') ||
                    error.message?.includes('user not found') ||
                    error.message?.includes('resource does not exist')) {
                    console.warn('Suppressing user not found error from user display:', error.message);
                    return false;
                }

                // Don't show 404 errors for user-related operations when not authenticated
                if (error.response?.status === 404 &&
                    (error.message?.includes('User') ||
                        error.message?.includes('user') ||
                        error.config?.url?.includes('/auth/user/') ||
                        error.config?.url?.includes('/api/auth/user/'))) {
                    console.warn('Suppressing 404 user error from user display:', error.message);
                    return false;
                }

                // Don't show "User not found" errors when user is authenticated but not in database
                // This happens during signup or when backend sync fails
                if (error.message?.includes('User not found') &&
                    error.response?.status === 404 &&
                    (error.config?.url?.includes('/auth/user/') ||
                        error.config?.url?.includes('/api/auth/user/'))) {
                    console.warn('Suppressing authenticated user not found in database error:', error.message);
                    return false;
                }

                // Show other errors by default
                console.log('Showing error to user:', error.message);
                return true;
            };

            // Log all errors for debugging
            console.error('Socket call error caught:', {
                type,
                severity,
                message: error.message,
                originalError: error,
                willShowToUser: shouldShowToUser(error, type, severity)
            });

            // Only add to global state if it should be shown to user
            if (shouldShowToUser(error, type, severity)) {
                addError(error, {
                    type: ERROR_TYPES.SOCKET,
                    source: 'socket',
                    ...options
                });
            }

            throw error;
        }
    }, [addError, determineErrorType, determineErrorSeverity]);

    // Get errors by type
    const getErrorsByType = useCallback((type) => {
        return state.errors.filter(error => error.type === type);
    }, [state.errors]);

    // Get errors by severity
    const getErrorsBySeverity = useCallback((severity) => {
        return state.errors.filter(error => error.severity === severity);
    }, [state.errors]);

    // Check if there are any errors
    const hasErrors = useCallback(() => {
        return state.errors.length > 0;
    }, [state.errors]);

    // Check if there are critical errors
    const hasCriticalErrors = useCallback(() => {
        return state.errors.some(error => error.severity === ERROR_SEVERITY.CRITICAL);
    }, [state.errors]);

    const contextValue = {
        // State
        errors: state.errors,
        isErrorModalOpen: state.isErrorModalOpen,
        currentError: state.currentError,
        errorCount: state.errorCount,

        // Actions
        addError,
        removeError,
        clearErrors,
        closeErrorModal,

        // Handlers
        handleAsync,
        handleApiCall,
        handleSocketCall,

        // Utilities
        getErrorsByType,
        getErrorsBySeverity,
        hasErrors,
        hasCriticalErrors,

        // Constants
        ERROR_TYPES,
        ERROR_SEVERITY
    };

    return (
        <ErrorContext.Provider value={contextValue}>
            {children}
        </ErrorContext.Provider>
    );
};

// Custom hook to use error context
export const useError = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

// Higher-order component for error handling
export const withErrorHandling = (Component, options = {}) => {
    return (props) => {
        const { handleAsync, addError } = useError();

        const wrappedComponent = (
            <Component
                {...props}
                handleAsync={handleAsync}
                addError={addError}
            />
        );

        return wrappedComponent;
    };
};

export default ErrorContext; 